import React, { useState, useEffect, useRef, useCallback } from 'react';

import { v4 as uuidv4 } from "uuid";
import {
  Add,
  DeleteForever,
} from "@mui/icons-material";
import { Button, Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  FeatureSource,
  ModelBase,
} from "@models";
import { 
  Item, 
  BasicItem,
  Ammunition,
  Weapon,
  Armor,
  Container,
  RollPlus,
  UpgradableNumber,
  DeckItem,
  GemItem,
  ArtisanTool,
  ArtisanToolUtilize,
  ArtisanToolCraftable,
  SpellLimitation,
  ARMOR_TYPE_OPTIONS,
  WEAPON_KEYWORDS,
  WEAPON_KEYWORD_OPTIONS,
} from "@dndModels";
import { 
  ABILITY_SCORES,
  ALL_DRAGONS,
  AMMUNITION_TYPES,
  CREATURE_TYPES,
  DAMAGE_TYPES,
  DRAGON_TYPES,
  ITEM_TYPES,
  RARITY_NUM_TO_STR_MAP,
  RARITY_OPTIONS,
  REFRESH_RULES,
  SCHOOLS,
  SPELL_LEVELS,
  WEAPON_TYPES, 
} from "@dndModels/Constants";
import { AbilityScoreType, AmmunitionType, ItemType, RefreshRuleType, WeaponType } from "@dndModels/Types";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import SelectObjectFieldBox from "@components/input/selects/SelectObjectFieldBox";
import LoadingBox from "@components/input/LoadingBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import DamageInput from "@dndComponents/other/DamageInput";
import FeatureSourceInput from "@dndComponents/FeatureSourceInput";
import EditableLabel from "@components/input/strings/EditableLabel";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CheckBox from "@components/input/CheckBox";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import StringsBox from "@components/input/strings/StringsBox";
import SpellLimitInput from "@dndComponents/limitations/SpellLimitInput";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function ItemEdit({ obj: param_obj }: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [obj, setObj] = useState<Item>(param_obj instanceof Item ? param_obj : api.applyWorkingOn(new Weapon()));
  const [reach_id, setReachId] = useState("");
  const [range_id, setRangeId] = useState("");
  const [melee_id, setMeleeId] = useState("");
  const [thrown_id, setThrownId] = useState("");
  const [versatile_id, setVersatileId] = useState("");
  const [ammo_id, setAmmoId] = useState("");
  const [attack_type, setAttackType] = useState("");
  const [change_time, setChangeTime] = useState(0);
  const [adding_name_for_rarity, setAddingNameForRarity] = useState("");
  // const [recipe_view, setRecipeView] = useState("Standard");
  // const [changed_campaign_recipe, setChangedCampaignRecipe] = useState<CampaignRecipe | null>(null);
  // const [campaign_recipe, setCampaignRecipe] = useState<CampaignRecipe | null>(null);
  // const [using_standard, setUsingStandard] = useState(false);
  const [cards_view, setCardsView] = useState("Manage");
  const [cards_parse, setCardsParse] = useState("");
  
  const parseRef = useRef<HTMLDivElement>(null);

  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  

  const load = useCallback( async () => {
    let new_reach_id = "";
    let new_range_id = "";
    let new_melee_id = "";
    let new_thrown_id = "";
    let new_versatile_id = "";
    let new_ammo_id = "";
    WEAPON_KEYWORDS.filter(o => ["Versatile","Melee","Reach","Range","Ranged","Thrown","Ammunition"].includes(o.name)).forEach(wk => {
      if (wk.name === "Reach") {
        new_reach_id = wk._id;
      } else if (wk.name === "Melee") {
        new_melee_id = wk._id;
      } else if (wk.name === "Range" || wk.name === "Ranged") {
        new_range_id = wk._id;
      } else if (wk.name === "Thrown") {
        new_thrown_id = wk._id;
      } else if (wk.name === "Ammunition") {
        new_ammo_id = wk._id;
      } else {
        new_versatile_id = wk._id;
      }
    });
    setReachId(new_reach_id);
    setRangeId(new_range_id);
    setMeleeId(new_melee_id);
    setThrownId(new_thrown_id);
    setAmmoId(new_ammo_id);
    setVersatileId(new_versatile_id);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const parseCards = () => {
    const new_obj = obj;
    if (new_obj instanceof DeckItem) {
      new_obj.cards = [];
      const parse_html = parseRef.current as HTMLElement;
      const tr_finder = parse_html.getElementsByTagName("tr");
      for (let i = 0; i < tr_finder.length; ++i) {
        const tr = tr_finder[i];
        const td_finder = tr.getElementsByTagName("td");
        if (td_finder.length > 1 && td_finder[0].textContent && td_finder[1].textContent) {
          const name = td_finder[0].textContent;
          const description = td_finder[1].textContent;
          const option: { true_id: string, name: string, description: string, reshuffle: boolean } = {
            true_id: uuidv4().toString(),
            name,
            description,
            reshuffle: true
          };
          new_obj.cards.push(option);
        }
      }
      setObj(new_obj);
    }
    setChangeTime(common_util.get_time_number());
  }

  const renderDetails = () => {
    let return_me: any = null;
    // const disabled = api.user === null || !api.online || (!api.user.admin && obj.owner_id !== api.user._id);
    switch (obj.item_type) {
      case "Armor": 
        const armor = obj as Armor;
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 4
              }}>
              <SelectStringBox
                name="Armor Type" 
                options={ARMOR_TYPE_OPTIONS}
                value={ armor.armor_type_id ? armor.armor_type_id : "" } 
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj as Armor;
                  new_obj.armor_type_id = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <StringBox
                name="Base Armor Class"
                value={ `${armor.base_armor_class}` } 
                type="number"
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj as Armor;
                  new_obj.base_armor_class = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <SelectStringBox
                name="Stealth Disadvantage"
                value={ armor.stealth_disadvantage === null ? "Inherit From Armor Type" : (armor.stealth_disadvantage ? "True" : "False") } 
                options={["Inherit From Armor Type","True","False"]}
                disabled={disabled}
                onChange={(changed: "Inherit From Armor Type" | "True" | "False") => {
                  const new_obj = obj as Armor;
                  switch (changed) {
                    case "Inherit From Armor Type": new_obj.stealth_disadvantage = null; break;
                    case "False": new_obj.stealth_disadvantage = false; break;
                    case "True": new_obj.stealth_disadvantage = true; break;
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <StringBox
                name="Strength Requirement (0 for none)"
                value={ `${armor.strength_requirement}` }
                type="number"
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj as Armor;
                  new_obj.strength_requirement = Math.max(0, +changed || 0);
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      break;
      case "Weapon":
        const weapon = obj as Weapon;
        const attack_types = [];
        if (weapon.weapon_keyword_ids.indexOf(melee_id) !== -1) {
          attack_types.push("Melee");
        }
        if (weapon.weapon_keyword_ids.indexOf(range_id) !== -1) {
          attack_types.push("Ranged");
        }
        if (weapon.weapon_keyword_ids.indexOf(thrown_id) !== -1) {
          attack_types.push("Thrown");
        }
        if (weapon.weapon_keyword_ids.indexOf(versatile_id) !== -1) {
          attack_types.push("Versatile");
        }
        const use_attack_type = attack_types.length > 0 && !attack_types.includes(attack_type) ? attack_types[0] : attack_type;
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 4
              }}>
              <SelectStringBox 
                name="Weapon Keywords" 
                options={WEAPON_KEYWORD_OPTIONS}
                values={ weapon.weapon_keyword_ids } 
                multiple
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj as Weapon;
                  new_obj.weapon_keyword_ids = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <SelectObjectBox 
                name="Weapon Mastery Property" 
                data_type="weapon_mastery_property"
                value={ weapon.weapon_mastery_property_id } 
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj as Weapon;
                  new_obj.weapon_mastery_property_id = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <SelectStringBox
                name="Weapon Type" 
                value={ weapon.weapon_type } 
                options={WEAPON_TYPES}
                disabled={disabled}
                onChange={(changed: WeaponType) => {
                  const new_obj = obj as Weapon;
                  new_obj.weapon_type = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <ToggleButtonBox 
                name="Stackable"
                value={weapon.stackable}
                disabled={disabled}
                onToggle={() => {
                  const new_obj = obj as Weapon;
                  new_obj.stackable = !new_obj.stackable;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { weapon.stackable && 
              <Grid
                size={{
                  xs: 12
                }}>
                <StringBox 
                  value={`${weapon.bundle_size}`} 
                  name="Bundle Size"
                  type="number"
                  disabled={disabled}
                  onBlur={(value: number) => {
                    const new_obj = obj as Weapon;
                    new_obj.bundle_size = +value;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
            <Grid
              size={{
                xs: 12
              }}>
              <SelectStringBox 
                name="Attack Type to View"
                options={attack_types}
                value={use_attack_type}
                disabled={disabled}
                onChange={(changed: string) => {
                  setAttackType(changed);
                  setReloading(true);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              { renderAttackDamages(use_attack_type) }
            </Grid>
            { renderAmmunition() }
          </Grid>
        );
      break;
      case "Ammunition":
        const ammunition = obj as Ammunition;
        return_me = (
          <Grid container spacing={0} direction="row" key="1">
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox
                value={`${ammunition.bundle_size}`} 
                name="Bundle Size"
                type="number"
                disabled={disabled}
                onBlur={(value: number) => {
                  const new_obj = obj as Ammunition;
                  new_obj.bundle_size = +value;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <SelectStringBox
                name="Ammunition Type" 
                options={AMMUNITION_TYPES}
                value={ammunition.ammunition_type}
                disabled={disabled}
                onChange={(changed: AmmunitionType) => {
                  const new_obj = obj as Ammunition;
                  new_obj.ammunition_type = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      break;
      case "Container":
        const container = obj as Container;
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox  
                name="Max Weight (in lbs)"
                value={`${container.max_weight}`}
                type="number"
                disabled={disabled}
                onBlur={(value: number) => {
                  const new_obj = obj as Container;
                  new_obj.max_weight = +value;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <SelectStringBox
                name="Item Types"
                options={ITEM_TYPES}
                extra_options={["ALL"]}
                values={container.item_types}
                multiple
                disabled={disabled}
                auto_sort
                onChange={(changed: string[]) => {
                  const new_obj = obj as Container;
                  new_obj.item_types = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <SelectObjectBox
                name="Items" 
                data_type="item"
                values={container.base_item_ids}
                multiple
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj as Container;
                  new_obj.base_item_ids = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      break;
      case "Deck":
        const deck = obj as DeckItem;
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <SelectObjectBox
                name="Deck"
                data_type="game_deck"
                extra_options={["None"]}
                value={deck.deck_id === "" ? "None" : deck.deck_id}
                onChange={(changed: string) => {
                  const new_obj = obj as DeckItem;
                  new_obj.deck_id = changed === "None" ? "" : changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { deck.deck_id === "" &&
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <CenteredMenu
                    options={["Manage","Parse"]}
                    selected={cards_view}
                    onSelect={setCardsView}
                  />
                </Grid>
                { cards_view === "Manage" ? 
                  <>
                    <Grid
                      className="header"
                      size={{
                        xs: 6
                      }}>
                      <AddButton
                        onClick={() => {
                          const new_obj = obj as DeckItem;
                          new_obj.cards.push({ name: "", description: "", true_id: uuidv4().toString(), reshuffle: true });
                          setObj(new_obj);
                          setChangeTime(common_util.get_time_number());
                        }}
                      /> Cards
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <ButtonBox
                        name="Reshuffle All"
                        onClick={() => {
                          const new_obj = obj as DeckItem;
                          for (const card of new_obj.cards) {
                            card.reshuffle = true;
                          }
                          setObj(new_obj);
                          setChangeTime(common_util.get_time_number());
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <ButtonBox
                        name="Un-Reshuffle All"
                        onClick={() => {
                          const new_obj = obj as DeckItem;
                          for (const card of new_obj.cards) {
                            card.reshuffle = false;
                          }
                          setObj(new_obj);
                          setChangeTime(common_util.get_time_number());
                        }}
                      />
                    </Grid>
                    { deck.cards.map((card, key) => {
                      return (
                        <Grid
                          key={key}
                          container
                          spacing={0}
                          direction="row"
                          size={{
                            xs: 12
                          }}>
                          <Grid
                            size={{
                              xs: 6
                            }}>
                            <StringBox
                              name="Card"
                              value={card.name}
                              onBlur={(changed: string) => {
                                const new_obj = obj as DeckItem;
                                const new_card = new_obj.cards.find(o => o.true_id === card.true_id);
                                if (new_card) {
                                  new_card.name = changed;
                                }
                                setObj(new_obj);
                                setChangeTime(common_util.get_time_number());
                              }}
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 6
                            }}>
                            <StringBox
                              name="Description"
                              value={card.description}
                              onBlur={(changed: string) => {
                                const new_obj = obj as DeckItem;
                                const new_card = new_obj.cards.find(o => o.true_id === card.true_id);
                                if (new_card) {
                                  new_card.description = changed;
                                }
                                setObj(new_obj);
                                setChangeTime(common_util.get_time_number());
                              }}
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 6
                            }}>
                            <CheckBox
                              name="Reshuffle"
                              value={card.reshuffle}
                              onChange={(changed: boolean) => {
                                const new_obj = obj as DeckItem;
                                const new_card = new_obj.cards.find(o => o.true_id === card.true_id);
                                if (new_card) {
                                  new_card.reshuffle = changed;
                                }
                                setObj(new_obj);
                                setChangeTime(common_util.get_time_number());
                              }}
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 6
                            }}>
                            <DeleteButton
                              onClick={() => {
                                const new_obj = obj as DeckItem;
                                new_obj.cards = new_obj.cards.filter(o => o.true_id !== card.true_id);
                                setObj(new_obj);
                                setChangeTime(common_util.get_time_number());
                              }}
                            />
                          </Grid>
                        </Grid>
                      );
                    })}
                  </>
                :
                  <Grid
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      style={{ maxHeight: "300px", overflowY: "scroll" }}
                      size={{
                        xs: 9
                      }}>
                      <StringBox 
                        name="Parser"
                        value={cards_parse}
                        multiline
                        onBlur={(changed: string) => {
                          while (changed.includes("–")) {
                            changed = changed.replace("–","-");
                          }
                          setCardsParse(changed);
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <Button variant="contained" color="primary"
                        onClick={parseCards}>
                        Parse
                      </Button>
                    </Grid>
                    <Grid
                      style={{ maxHeight: "300px", overflowY: "scroll" }}
                      size={{
                        xs: 12
                      }}>
                      <div ref={parseRef} dangerouslySetInnerHTML={{ __html: cards_parse }}></div>
                    </Grid>
                  </Grid>
                }
              </>
            }
          </Grid>
        );
      break;
      case "Gem":
        const gem = obj as GemItem;
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              className="header"
              size={{
                xs: 12
              }}>
              Size Options 
              <AddButton
                onClick={() => {
                  const new_obj = obj as GemItem;
                  const cost = new_obj.sizes.length === 0 ? obj.cost : new_obj.sizes[new_obj.sizes.length - 1].cost;
                  const weight = new_obj.sizes.length === 0 ? obj.weight : new_obj.sizes[new_obj.sizes.length - 1].weight;
                  const ac = new_obj.sizes.length === 0 ? 0 : new_obj.sizes[new_obj.sizes.length - 1].ac;
                  const hp = new_obj.sizes.length === 0 ? 0 : new_obj.sizes[new_obj.sizes.length - 1].hp;
                  new_obj.sizes.push({ true_id: uuidv4().toString(), name: "", cost, weight, ac, hp, extraction: "" });
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { gem.sizes.map((size, key) => {
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox
                      name="Size"
                      value={size.name}
                      onBlur={(changed: string) => {
                        const new_obj = obj as GemItem;
                        const new_size = new_obj.sizes.find(o => o.true_id === size.true_id);
                        if (new_size) {
                          new_size.name = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox
                      name="Cost"
                      value={`${size.cost}`}
                      type="number"
                      onBlur={(changed: string) => {
                        const new_obj = obj as GemItem;
                        const new_size = new_obj.sizes.find(o => o.true_id === size.true_id);
                        if (new_size) {
                          new_size.cost = +changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox
                      name="Weight"
                      value={`${size.weight}`}
                      type="number"
                      onBlur={(changed: string) => {
                        const new_obj = obj as GemItem;
                        const new_size = new_obj.sizes.find(o => o.true_id === size.true_id);
                        if (new_size) {
                          new_size.weight = +changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox
                      name="AC"
                      value={`${size.ac}`}
                      type="number"
                      onBlur={(changed: string) => {
                        const new_obj = obj as GemItem;
                        const new_size = new_obj.sizes.find(o => o.true_id === size.true_id);
                        if (new_size) {
                          new_size.ac = +changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox
                      name="HP"
                      value={`${size.hp}`}
                      type="number"
                      onBlur={(changed: string) => {
                        const new_obj = obj as GemItem;
                        const new_size = new_obj.sizes.find(o => o.true_id === size.true_id);
                        if (new_size) {
                          new_size.hp = +changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <DeleteButton
                      onClick={() => {
                        const new_obj = obj as GemItem;
                        new_obj.sizes = new_obj.sizes.filter(o => o.true_id !== size.true_id);
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <StringBox
                      name="Extraction"
                      value={size.extraction}
                      onBlur={(changed: string) => {
                        const new_obj = obj as GemItem;
                        const new_size = new_obj.sizes.find(o => o.true_id === size.true_id);
                        if (new_size) {
                          new_size.extraction = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        );
      break;
      // case "Prosthesis":
      //   return_me = renderProsthesis();
      // break;
      case "Artisan's Tools":
        const artisan_tool = obj as ArtisanTool;
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 6
              }}>
              <SelectObjectBox
                name="Tool Type"
                data_type="tool"
                value={artisan_tool.tool_id}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj as ArtisanTool;
                  new_obj.tool_id = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <SelectStringBox
                name="Ability"
                options={ABILITY_SCORES}
                value={artisan_tool.ability}
                disabled={disabled}
                onChange={(changed: AbilityScoreType) => {
                  const new_obj = obj as ArtisanTool;
                  new_obj.ability = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              className="header"
              size={{
                xs: 12
              }}>
              Utilize
              <AddButton
                disabled={disabled}
                onClick={() => {
                  const new_obj = obj as ArtisanTool;
                  new_obj.utilize.push(new ArtisanToolUtilize());
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { artisan_tool.utilize.map((util, key) => {
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 9
                    }}>
                    <StringBox
                      name="Description"
                      value={util.description}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj as ArtisanTool;
                        const new_util = new_obj.utilize.find(o => o.true_id === util.true_id);
                        if (new_util) {
                          new_util.description = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox
                      name="DC"
                      value={`${util.dc}`}
                      type="number"
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj as ArtisanTool;
                        const new_util = new_obj.utilize.find(o => o.true_id === util.true_id);
                        if (new_util) {
                          new_util.dc = +changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 1
                    }}>
                    <DeleteButton
                      disabled={disabled}
                      onClick={() => {
                        const new_obj = obj as ArtisanTool;
                        new_obj.utilize = new_obj.utilize.filter(o => o.true_id !== util.true_id);
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
            <Grid
              className="header"
              size={{
                xs: 12
              }}>
              Craft
              <AddButton
                label="Item"
                disabled={disabled}
                onClick={() => {
                  const new_obj = obj as ArtisanTool;
                  new_obj.craftables.push(new ArtisanToolCraftable());
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
              <AddButton
                label="Group"
                disabled={disabled}
                onClick={() => {
                  const new_obj = obj as ArtisanTool;
                  new_obj.craftables.push(new ArtisanToolCraftable({ group: true }));
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { artisan_tool.craftables.map((craftable, key) => {
              const updateCraftable = (update: (o: ArtisanToolCraftable) => void) => {
                const new_obj = obj as ArtisanTool;
                const new_craftable = new_obj.craftables.find(o => o.true_id === craftable.true_id);
                if (new_craftable) {
                  update(new_craftable);
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              };
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  { craftable.group ?
                    <>
                      <Grid
                        size={{
                          xs: 3
                        }}>
                        <StringBox
                          name="Name"
                          value={craftable.name}
                          disabled={disabled}
                          onBlur={(changed: string) => {
                            updateCraftable(o => { o.name = changed; });
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 2
                        }}>
                        <SelectStringBox
                          name="Item Type"
                          options={ITEM_TYPES}
                          value={craftable.item_type}
                          auto_sort
                          disabled={disabled}
                          onChange={(changed: string) => {
                            updateCraftable(o => { o.item_type = changed; });
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 2
                        }}>
                        <StringBox
                          name="Category"
                          value={craftable.category}
                          disabled={disabled}
                          onBlur={(changed: string) => {
                            updateCraftable(o => { o.category = changed; });
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 4
                        }}>
                        <SelectObjectBox
                          name="Except"
                          data_type="item"
                          values={craftable.except_item_ids}
                          multiple
                          disabled={disabled}
                          onChange={(changed: string[]) => {
                            updateCraftable(o => { o.except_item_ids = changed; });
                          }}
                        />
                      </Grid>
                    </>
                  :
                    <Grid
                      size={{
                        xs: 11
                      }}>
                      <SelectObjectBox
                        name="Item"
                        data_type="item"
                        value={craftable.item_id}
                        auto_sort
                        disabled={disabled}
                        onChange={(changed: string) => {
                          updateCraftable(o => { o.item_id = changed; });
                        }}
                      />
                    </Grid>
                  }
                  <Grid
                    size={{
                      xs: 1
                    }}>
                    <DeleteButton
                      disabled={disabled}
                      onClick={() => {
                        const new_obj = obj as ArtisanTool;
                        new_obj.craftables = new_obj.craftables.filter(o => o.true_id !== craftable.true_id);
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        );
      break;
      case "Tool": case "Gaming Set": case "Musical Instrument":
        const tool = obj as BasicItem;
        return_me = (
          <Grid key="1">
            <SelectObjectBox
              name="Tool Type"
              data_type="tool"
              value={tool.tool_id}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj as BasicItem;
                new_obj.tool_id = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        );
      break;
      default:
        const basic_item = obj as BasicItem;
        return_me = (
          <Grid key="1" container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <SelectStringBox
                name="Worn Type"
                options={["None","Head","Chest","Hands","Feet","Cape","Ring","Belt"]}
                value={basic_item.worn_type}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj as BasicItem;
                  new_obj.worn_type = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <ToggleButtonBox 
                name="Stackable"
                value={basic_item.stackable}
                disabled={disabled}
                onToggle={() => {
                  const new_obj = obj as BasicItem;
                  new_obj.stackable = !new_obj.stackable;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { basic_item.stackable && 
              <Grid
                size={{
                  xs: 12
                }}>
                <StringBox 
                  value={`${basic_item.bundle_size}`} 
                  name="Bundle Size"
                  type="number"
                  disabled={disabled}
                  onBlur={(value: number) => {
                    const new_obj = obj as BasicItem;
                    new_obj.bundle_size = +value;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
          </Grid>
        );
      break;
    }
    return return_me;
  }

  const renderAttackDamages = (use_attack_type: string) => {
    const weapon = obj as Weapon;
    let attack_damages: RollPlus[] = [];
    if (weapon.attacks[use_attack_type]) {
      attack_damages = weapon.attacks[use_attack_type].rolls as RollPlus[];
    }
    return (
      <Grid container spacing={0} direction="row">
        { weapon.weapon_keyword_ids.includes(reach_id) ?
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Extra Reach (in spaces)"
              value={`${weapon.reach}`}
              type="number"
              disabled={disabled}
              onBlur={(value: number) => {
                const new_obj = obj as Weapon;
                new_obj.reach = +value;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        : ["Ranged","Thrown"].includes(use_attack_type) &&
          <>
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox 
                name="Range (in feet)"
                value={`${weapon.range}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: number) => {
                  const new_obj = obj as Weapon;
                  new_obj.range = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox 
                name="Long Range (in feet)"
                value={`${weapon.range2}`}
                type="number"
                disabled={disabled}
                onBlur={(changed: number) => {
                  const new_obj = obj as Weapon;
                  new_obj.range2 = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </>
        }
        <Grid
          size={{
            xs: 4
          }}>
        </Grid>
        { !disabled &&
          <Grid
            size={{
              xs: 4
            }}>
            <Tooltip title={`Delete ${use_attack_type}`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  const new_obj = obj as Weapon;
                  delete new_obj.attacks[use_attack_type];
                  setObj(new_obj);
                  setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}>
                <DeleteForever/>
              </Fab>
            </Tooltip>
          </Grid>
        }
        { !disabled &&
          <Grid
            size={{
              xs: 4
            }}>
            <Tooltip title={`Add Damage`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  const new_obj = obj as Weapon;
                  if (!new_obj.attacks[use_attack_type]) {
                    new_obj.attacks[use_attack_type] = {
                      rolls: [],
                      uses_proficiency_id: ""
                    }
                  }
                  const damage = new RollPlus();
                  damage.count = 1;
                  new_obj.attacks[use_attack_type].rolls.push(damage);
                  setObj(new_obj);
                  setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}>
                <Add/>
              </Fab>
            </Tooltip>
          </Grid>
        }
        { !reloading && attack_damages.map((damage, key) => {
          // weapon.attack_damages.map((damage, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <DamageInput
                obj={damage}
                disabled={disabled}
                change_time={change_time}
                onChange={(changed: RollPlus) => {
                  const new_obj = obj as Weapon;
                  const damage_finder = new_obj.attacks[use_attack_type].rolls.find(o => o.true_id === damage.true_id);
                  if (damage_finder) {
                    damage_finder.copy(changed);
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
                onDelete={() => {
                  const new_obj = obj as Weapon;
                  new_obj.attacks[use_attack_type].rolls = new_obj.attacks[use_attack_type].rolls.filter(o => o.true_id !== damage.true_id);
                  if (new_obj.attacks[use_attack_type].rolls.length === 0) {
                    delete new_obj.attacks[use_attack_type];
                  }
                  setObj(new_obj);
                  setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }

  const renderAmmunition = () => {
    const weapon = obj as Weapon;
    if (weapon.weapon_keyword_ids.indexOf(ammo_id) !== -1) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name="Ammunition Type" 
            options={AMMUNITION_TYPES}
            value={weapon.ammunition_type}
            disabled={disabled}
            onChange={(changed: AmmunitionType) => {
              const new_obj = obj as Weapon;
              new_obj.ammunition_type = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  // const renderProsthesis = () => {
  //   const prosthesis = obj as Prosthesis;
  //   return (
  //     <Grid item xs={12}>
  //       <SelectStringBox
  //         name="Body Parts" 
  //         options={["Arm","Leg","Tail"]}
  //         values={prosthesis.body_locations}
  //         disabled={disabled}
  //         multiple
  //         onChange={(changed: string[]) => {
  //           const new_obj = obj as Prosthesis;
  //           new_obj.body_locations = changed;
  //           setObj(new_obj);
  //           setChangeTime(common_util.get_time_number());
  //         }}
  //       />
  //     </Grid>
  //   );
  // }

  const renderNameOptions = () => {
    if (obj.min_rarity < obj.max_rarity) {
      const name_rarities = Object.keys(obj.rarity_names).map(o => +o);
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Name Formula"
              value={obj.name_formula}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.name_formula = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Add Name for Rarity"
              options={RARITY_OPTIONS.filter(o => +o.id >= obj.min_rarity && +o.id <= obj.max_rarity && !name_rarities.includes(+o.id))}
              value={adding_name_for_rarity}
              onChange={setAddingNameForRarity}
            />
          </Grid>
          <Grid
            className="header"
            size={{
              xs: 6
            }}>
            <AddButton
              disabled={disabled || obj.name_formula.length > 0 || adding_name_for_rarity === ""}
              onClick={() => {
                const new_obj = obj;
                new_obj.rarity_names[+adding_name_for_rarity] = obj.name + " " + RARITY_NUM_TO_STR_MAP[+adding_name_for_rarity];
                setAddingNameForRarity("");
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
                setReloading(true);
              }}
            />
          </Grid>
          { !reloading && name_rarities.map((rarity, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 9
                  }}>
                  <StringBox
                    name={`Name for ${RARITY_NUM_TO_STR_MAP[rarity]}`}
                    value={obj.rarity_names[rarity]}
                    disabled={disabled}
                    onBlur={(changed: string) => {
                      const new_obj = obj;
                      new_obj.rarity_names[rarity] = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DeleteButton
                    disabled={disabled}
                    onClick={() => {
                      const new_obj = obj;
                      delete new_obj.rarity_names[rarity];
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                      setReloading(true);
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}
        </>
      );
    } else if (obj.extra_base_type !== "") {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Name Formula"
            value={obj.name_formula}
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.name_formula = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderExtraLimits = () => {
    if (obj.extra_base_type === "Spell") {
      const extra_base_limits = obj.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Spell Levels"
              values={extra_base_limits.levels}
              options={[
                "Any",
                ...SPELL_LEVELS
              ]}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.extra_base_limits.levels = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Schools"
              values={extra_base_limits.schools}
              options={[
                "Any",
                ...SCHOOLS
              ]}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.extra_base_limits.schools = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (obj.extra_base_type === "Strings") {
      const extra_base_limits = obj.extra_base_limits as string[];
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <StringsBox
            name="Strings"
            values={extra_base_limits}
            disabled={disabled}
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.extra_base_limits = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      );
    } else if (obj.extra_base_type !== "None") {
      // debugger;
      const extra_base_limits = obj.extra_base_limits as string[];
      let options = ["Any"];
      let label = "";
      switch(obj.extra_base_type) {
        case "Ability Score":
          label = "Ability Scores";
          options = ABILITY_SCORES;
        break;
        case "Damage Type":
          label = "Damage Types";
          options = DAMAGE_TYPES;
        break;
        case "School of Magic":
          label = "Schools";
          options = SCHOOLS;
        break;
        case "Dragon Type":
          label = "Dragon Types";
          options = DRAGON_TYPES;
        break;
        case "Dragon Breed":
          label = "Dragon Breeds";
          options = ALL_DRAGONS;
        break;
        case "Creature Type":
          label = "Creature Types";
          options = CREATURE_TYPES;
        break;
        // case "Strings":

        // break;
        // case "Spell List":
        //   options = [
        //     "Any",
        //     ...DAMAGE_TYPES
        //   ];
        // break;
      }
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name={label}
            values={extra_base_limits}
            options={options}
            extra_options={["Any"]}
            disabled={disabled}
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.extra_base_limits = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  // const getDefaultRecipe = () => {
  //   const recipe = new Recipe();
  //   recipe.name = "Mad HB Default";
  //   let cr = 1;
  //   if (obj.item_type === "Ammunition" || obj.item_type === "Consumable" || obj.item_type === "Potion" || obj.item_type === "Poison" || obj.item_type === "Dust") {
  //     const rarityToCRMap: { [rarity: number]: number } = {
  //       0: 1, // Common
  //       1: 2, // Uncommon
  //       2: 5, // Rare
  //       3: 8, // Very-Rare
  //       4: 15 // Legendary
  //     }
  //     cr = rarityToCRMap[obj.min_rarity];
  //   } else if (obj.item_type === "Scroll") {
  //     if (obj.extra_base_type === "Spell") {
  //       const extra_base_limits = obj.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
  //       if (extra_base_limits.levels.length === 1) {
  //         cr = +extra_base_limits.levels[0] * 2;
  //       } else {
  //         const rarityToCRMap: { [rarity: number]: number } = {
  //           0: 1, // Common
  //           1: 2, // Uncommon
  //           2: 5, // Rare
  //           3: 8, // Very-Rare
  //           4: 15 // Legendary
  //         }
  //         cr = rarityToCRMap[obj.min_rarity];
  //       }
  //     } else {
  //       const rarityToCRMap: { [rarity: number]: number } = {
  //         0: 1, // Common
  //         1: 2, // Uncommon
  //         2: 5, // Rare
  //         3: 8, // Very-Rare
  //         4: 15 // Legendary
  //       }
  //       cr = rarityToCRMap[obj.min_rarity];
  //     }
  //   } else {
  //     const rarityToCRMap: { [rarity: number]: number } = {
  //       0: 2, // Common
  //       1: 4, // Uncommon
  //       2: 8, // Rare
  //       3: 12, // Very-Rare
  //       4: 18 // Legendary
  //     }
  //     cr = rarityToCRMap[obj.min_rarity];
  //   }
  //   if (cr) {
  //     recipe.cr = cr;
  //     const animus = new RecipeComponent();
  //     animus.component_type = "Animus";
  //     recipe.components.push(animus);
  //     switch (obj.item_type) {
  //       case "Potion":
  //       case "Poison":
  //         if (true) {
  //           const organ = new RecipeComponent();
  //           organ.component_type = "Organ"; // or Ash
  //           recipe.components.push(organ);
  //           const fluid = new RecipeComponent();
  //           fluid.component_type = "Fluid";
  //           recipe.components.push(fluid);
  //         }
  //       break;
  //       case "Scroll":
  //         if (true) {
  //           const hide = new RecipeComponent();
  //           hide.component_type = "Hide";
  //           recipe.components.push(hide);
  //           const fluid = new RecipeComponent();
  //           fluid.component_type = "Fluid";
  //           recipe.components.push(fluid);
  //         }
  //       break;
  //     }
  //     const component_count = obj.min_rarity + 2;
  //     while (recipe.components.length < component_count) {
  //       const comp = new RecipeComponent();
  //       recipe.components.push(comp);
  //     }
  //   }
  //   const new_obj = obj;
  //   new_obj.recipes.push(recipe);
  //   setObj(new_obj);
  //   setChangeTime(common_util.get_time_number());
  // }

  // const saveRecipes = () => {
  //   if (changed_campaign_recipe) {
  //     if (api.user && 
  //       changed_campaign_recipe && 
  //       api.recipes_for_campaign &&
  //       (api.user.admin || 
  //         api.recipes_for_campaign.owner_id === api.user._id)) {
  //       setProcessing(true);
  //       if (changed_campaign_recipe._id === "") {
  //         if (!api.user.admin) {
  //           changed_campaign_recipe.owner_id = api.user._id;
  //         } else {
  //           changed_campaign_recipe.owner_id = "";
  //         }
  //         changed_campaign_recipe.campaign_id = api.recipes_for_campaign._id;
  //         changed_campaign_recipe.for_type = "Item";
  //         changed_campaign_recipe.for_id = obj._id;
  //       }
  //       api.upsertObject("campaign_recipe", changed_campaign_recipe).then(res => {
  //         if (res && res.body.id) {
  //           changed_campaign_recipe._id = res.body.id;
  //         }
  //         setProcessing(false);
  //         setCampaignRecipe(changed_campaign_recipe);
  //       });
  //     }
  //   }
  // }

  // const resetRecipes = () => {
  //   if (campaign_recipe) {
  //     setChangedCampaignRecipe(campaign_recipe.clone());
  //   }
  // }

  const height = window.innerHeight - 105;
  if (!fetched || loading) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else {
    return (
      <FeatureSourceInput 
        obj={obj}
        data_type="item"
        disabled={disabled}
        change_time={change_time}
        onChange={(changed: FeatureSource) => {
          if (changed instanceof Item) {
            const new_obj = obj;
            new_obj.copy(changed);
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }
        }}
        extra_menu_options={{"Attributes": 
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <SelectObjectFieldBox
                name="Environments"
                data_type="creature"
                field="environments"
                extra_options={["Any"]}
                values={obj.environments}
                multiple
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  if (changed.includes("Any")) {
                    if (new_obj.environments.includes("Any")) {
                      new_obj.environments = changed.filter(o => o !== "Any");
                    } else {
                      new_obj.environments = ["Any"];
                    }
                  } else {
                    new_obj.environments = changed;
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              {/* <SelectObjectFieldBox
                name="Settings"
                data_type="creature"
                field="settings"
                extra_options={["Any"]}
                values={obj.settings}
                multiple
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  if (changed.includes("Any")) {
                    if (new_obj.settings.includes("Any")) {
                      new_obj.settings = changed.filter(o => o !== "Any");
                    } else {
                      new_obj.settings = ["Any"];
                    }
                  } else {
                    new_obj.settings = changed;
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              /> */}
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <span className="header">
                Formulae
              </span>
              { !disabled &&
                <Tooltip title={`Create New Formula`}>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    onClick={ () => {
                      const new_obj = obj;
                      new_obj.formulae["New Formula"] = new UpgradableNumber();
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                      setReloading(true);
                    }}>
                    <Add/>
                  </Fab>
                </Tooltip>
              }
            </Grid>
            { !reloading && Object.keys(obj.formulae).sort((a, b) => {
              return a.localeCompare(b);
            }).map((formula_key, key) => {
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <EditableLabel 
                      name="Formula Key"
                      value={formula_key}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        const old_value = new_obj.formulae[formula_key].clone();
                        delete new_obj.formulae[formula_key];
                        new_obj.formulae[changed] = old_value;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 10
                    }}>
                    <UpgradableNumberBox 
                      value={ obj.formulae[formula_key] as UpgradableNumber } 
                      name={ formula_key }
                      disabled={disabled}
                      onChange={(changed: UpgradableNumber) => {
                        const new_obj = obj;
                        new_obj.formulae[formula_key] = changed;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </Grid>,
          // "Recipes":
          // <Grid item xs={12} container spacing={0} direction="row">
          //   { obj.recipes.length === 0 &&
          //     <Grid item xs={12}>
          //       <ButtonBox
          //         name="Get Mad HB Default"
          //         disabled={disabled}
          //         onClick={getDefaultRecipe}
          //       />
          //     </Grid>
          //   }
          //   { obj._id !== "" && api.recipes_for_campaign && 
          //     <Grid item xs={12}>
          //       <CenteredMenu
          //         options={["Standard","Campaign"]}
          //         selected={recipe_view}
          //         onSelect={setRecipeView}
          //       />
          //     </Grid>
          //   }
          //   { recipe_view === "Standard" ? 
          //     <Grid item xs={12}>
          //       <RecipesInput
          //         recipes={obj.recipes}
          //         item_id={obj._id}
          //         disabled={disabled}
          //         onChange={(changed: Recipe[]) => {
          //           const new_obj = obj;
          //           for (const recipe of changed) {
          //             recipe.item_id = new_obj._id;
          //           }
          //           new_obj.recipes = changed;
          //           setObj(new_obj);
          //           setChangeTime(common_util.get_time_number());
          //         }}
          //       />
          //     </Grid>
          //   : changed_campaign_recipe &&
          //     <>
          //       <Grid item xs={12}>
          //         <ToggleButtonBox
          //           name="Use Standard"
          //           value={using_standard}
          //           onToggle={() => { setUsingStandard(!using_standard); }}
          //         />
          //       </Grid>
          //       { using_standard ? 
          //         <Grid item xs={12}>
          //           <SelectRecipeIndex
          //             item={obj}
          //             onUse={(changed: Recipe) => {
          //               const new_obj = changed_campaign_recipe;
          //               const clone = changed.clone();
          //               clone.name += " Copied from Standard";
          //               new_obj.recipes.push(clone);
          //               setChangedCampaignRecipe(new_obj);
          //               setChangeTime(common_util.get_time_number());
          //               setUsingStandard(false);
          //             }}
          //           />
          //         </Grid>
          //       :
          //         <>
          //           <Grid item xs={12}>
          //             <RecipesInput
          //               recipes={changed_campaign_recipe.recipes}
          //               item_id={obj._id}
          //               disabled={false}
          //               onChange={(changed: Recipe[]) => {
          //                 const new_obj = changed_campaign_recipe;
          //                 new_obj.recipes = changed;
          //                 setChangedCampaignRecipe(new_obj);
          //                 setChangeTime(common_util.get_time_number());
          //               }}
          //             />
          //           </Grid>
          //           <Grid item xs={12}>
          //             <Button
          //               variant="contained"
          //               color="primary"
          //               disabled={ processing }
          //               onClick={saveRecipes}>
          //               Save Recipes
          //             </Button>
          //             <Button
          //               variant="contained"
          //               disabled={processing}
          //               style={{ marginLeft: "4px" }}
          //               onClick={ () => { 
          //                 resetRecipes();
          //               }}>
          //               Cancel
          //             </Button>
          //           </Grid>
          //         </>
          //       }
          //     </>
          //   }
          // </Grid>
        }}
      >
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox 
              name="Weight (in lbs)"
              value={`${obj.weight}`} 
              type="number"
              disabled={disabled}
              onBlur={(value: string) => {
                const new_obj = obj;
                new_obj.weight = +value;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox 
              name="Cost (GP)"
              value={`${obj.cost}`} 
              type="number"
              disabled={disabled}
              onBlur={(value: string) => {
                const new_obj = obj;
                new_obj.cost = +value;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox
              name="Item Type"
              options={ITEM_TYPES}
              value={obj.item_type}
              auto_sort
              disabled={disabled}
              onChange={(changed: ItemType) => {
                let new_obj: Item | null = null;
                switch (changed) {
                  case "Armor":
                    const armor = new Armor();
                    armor.copyItem(obj);
                    new_obj = armor;
                  break;
                  case "Weapon":
                    const weapon = new Weapon();
                    weapon.copyItem(obj);
                    new_obj = weapon;
                  break;
                  case "Ammunition":
                    const ammo = new Ammunition();
                    ammo.copyItem(obj);
                    new_obj = ammo;
                  break;
                  case "Container":
                    const container = new Container();
                    container.copyItem(obj);
                    new_obj = container;
                  break;
                  case "Deck":
                    const deck = new DeckItem();
                    deck.copyItem(obj);
                    new_obj = deck;
                  break;
                  case "Gem":
                    const gem = new GemItem();
                    gem.copyItem(obj);
                    new_obj = gem;
                  break;
                  case "Artisan's Tools":
                    const artisan_tool = new ArtisanTool();
                    artisan_tool.copyItem(obj);
                    if (obj instanceof BasicItem) {
                      artisan_tool.tool_id = obj.tool_id;
                    }
                    new_obj = artisan_tool;
                  break;
                  // case "Prosthesis":
                  //   const prosthesis = new Prosthesis();
                  //   prosthesis.copyItem(obj);
                  //   new_obj = prosthesis;
                  // break;
                  default:
                    const basic_item = new BasicItem();
                    basic_item.copyItem(obj);
                    new_obj = basic_item;
                  break;
                }
                new_obj.item_type = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Min Rarity"
              options={RARITY_OPTIONS}
              value={`${obj.min_rarity}`}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.min_rarity = +changed;
                if (new_obj.min_rarity > new_obj.max_rarity) {
                  new_obj.max_rarity = new_obj.min_rarity;
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Max Rarity"
              options={RARITY_OPTIONS}
              value={`${obj.max_rarity}`}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.max_rarity = +changed;
                if (new_obj.min_rarity > new_obj.max_rarity) {
                  new_obj.min_rarity = new_obj.max_rarity;
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Extra Base Type"
              value={obj.extra_base_type}
              options={[
                "None",
                "Ability Score",
                "Damage Type",
                "School of Magic",
                "Dragon Type", // Metallic, Chromatic, Gem
                "Dragon Breed", // Metal, Color, Gem
                "Creature Type",
                "Strings",
                "Spell List",
                "Spell"
              ]}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.extra_base_type = changed;
                if (changed === "Spell") { 
                  new_obj.extra_base_limits = { levels: ["Any"], schools: ["Any"], onSpellList: ["Any"] }; 
                } else {
                  new_obj.extra_base_limits = ["Any"];
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          { renderExtraLimits() }
          { renderNameOptions() }
          { obj.extra_base_type === "Spell" &&
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 4
                }}>
                <UpgradableNumberBox 
                  name="Spell DC"
                  value={obj.spell_dc}
                  disabled={disabled}
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.spell_dc = changed.clone();
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <UpgradableNumberBox 
                  name="Spell Attack"
                  value={obj.spell_attack}
                  disabled={disabled}
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.spell_attack = changed.clone();
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <UpgradableNumberBox 
                  name="Spell Modifier"
                  value={obj.spell_mod}
                  disabled={disabled}
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.spell_mod = changed.clone();
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            </Grid>
          }
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              name="Ignore Proficiency Requirements" 
              value={obj.ignore_proficiency_requirements} 
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.ignore_proficiency_requirements = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              name="Requires Attunement" 
              value={obj.attunement} 
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.attunement = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              name="Spell Storing" 
              value={obj.spell_storing} 
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.spell_storing = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox 
              name="Charges"
              value={`${obj.charges}`} 
              type="number"
              disabled={disabled}
              onBlur={(value: string) => {
                const new_obj = obj;
                new_obj.charges = +value;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          { obj.spell_storing &&
            <>
              <Grid
                size={{
                  xs: 6
                }}>
                <CheckBox
                  name="Slot Based Count"
                  disabled={disabled}
                  value={obj.count_rules.slot_based_count}
                  onChange={(changed: boolean) => {
                    const new_obj = obj;
                    new_obj.count_rules.slot_based_count = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <UpgradableNumberBox
                  name="Max Spell Count"
                  disabled={disabled}
                  value={obj.count_rules.max_count}
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.count_rules.max_count = changed.clone();
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <SpellLimitInput
                  obj={obj.spell_limit} 
                  disabled={disabled}
                  onChange={(changed: SpellLimitation) => {
                    const new_obj = obj;
                    new_obj.spell_limit = changed.clone();
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            </>
          }
          { obj.charges > 0 && 
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 3
                }}>
                <CheckBox 
                  name="Invert Charges"
                  value={obj.invert_charges}
                  disabled={disabled}
                  onChange={(changed: boolean) => {
                    const new_obj = obj;
                    new_obj.invert_charges = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              { !obj.invert_charges && 
                <>
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <SelectStringBox 
                      name="Charge Refresh Rule"
                      value={obj.charge_refresh_rule}
                      options={REFRESH_RULES} 
                      disabled={disabled}
                      onChange={(changed: RefreshRuleType) => {
                        const new_obj = obj;
                        new_obj.charge_refresh_rule = changed;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox 
                      name="Dice Count"
                      value={`${obj.refresh_dice_count}`}
                      type="number"
                      disabled={disabled}
                      onBlur={(value: string) => {
                        const new_obj = obj;
                        new_obj.refresh_dice_count = +value;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox 
                      name="Dice Size"
                      value={`${obj.refresh_dice_size}`}
                      type="number"
                      disabled={disabled}
                      onBlur={(value: string) => {
                        const new_obj = obj;
                        new_obj.refresh_dice_size = +value;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <StringBox 
                      name="Flat"
                      value={`${obj.refresh_dice_flat}`}
                      type="number"
                      disabled={disabled}
                      onBlur={(value: string) => {
                        const new_obj = obj;
                        new_obj.refresh_dice_flat = +value;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                </>
              }
            </Grid>
          }
          <Grid
            size={{
              xs: 12
            }}>
            { renderDetails() }
          </Grid>
        </Grid>
      </FeatureSourceInput>
    ); 
  }
}
